var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useEffect, useRef, } from 'react';
import { Typography, Button, Badge, TileGroup, Tile, } from '@one-thd/sui-atomic-components';
// import { formatPhoneNumber } from '../../../utils/general';
import { areAddressesEqual, extractAddressFromFulfillmentGroup, isAddressEmpty, } from '../../../utils/order-details/OrderDetailsUtils';
import { useFulfillmentGroupContext, useOrderContext, } from '../../../hooks/useContexts';
import { EditDeliveryAddressErrorMessages, } from '../../../models/edit-delivery-address';
import { AlertBanner } from '../../shared/alert-banner/AlertBanner';
/**
 * Returns a list of delivery addresses to display, prioritizing the default and current addresses.
 *
 * @param {IDeliveryAddress[]} deliveryAddresses - Array of all delivery addresses.
 * @param {IDeliveryAddress | null} currentAddress - The current delivery address, or null if not set.
 * @returns {IDeliveryAddress[]} Array of addresses to show, ordered by priority.
 */
var getAddressesToShow = function (deliveryAddresses, currentAddress) {
    var defaultAddress = deliveryAddresses.find(function (addr) { return addr.isDefault; });
    // If there is no current address, show all addresses with the default address at the beginning.
    if (!currentAddress) {
        if (defaultAddress) {
            // Default at the beginning, then the rest (no duplicates)
            return __spreadArray([
                defaultAddress
            ], deliveryAddresses.filter(function (addr) { return addr.id !== defaultAddress.id; }), true);
        }
        // If there is no default, return the list as is
        return deliveryAddresses;
    }
    // If there is no default, show current + the rest
    if (!defaultAddress) {
        return __spreadArray([currentAddress], deliveryAddresses, true);
    }
    var filteredAddresses = deliveryAddresses.filter(function (addr) { return addr.id !== defaultAddress.id; });
    // If current and default are equal, show only current + the rest (no duplicates)
    if (areAddressesEqual(currentAddress, defaultAddress)) {
        return __spreadArray([__assign(__assign({}, currentAddress), { isDefault: true })], filteredAddresses, true);
    }
    // If current and default are different, show current, default, and the rest (no duplicates)
    return __spreadArray([currentAddress, defaultAddress], filteredAddresses, true);
};
export var EditDeliveryAddressChooseAddress = function (_a) {
    var setFirstName = _a.setFirstName, setLastName = _a.setLastName, setPhoneNumber = _a.setPhoneNumber, setStreetAddress = _a.setStreetAddress, setAddressLineTwo = _a.setAddressLineTwo, setZipCode = _a.setZipCode, setCityState = _a.setCityState, setCountry = _a.setCountry, handleClearAll = _a.handleClearAll, selectedAddressId = _a.selectedAddressId, setSelectedAddressId = _a.setSelectedAddressId, setIsAddressList = _a.setIsAddressList, setIsDefault = _a.setIsDefault, isExcludedAddress = _a.isExcludedAddress, isSameAddress = _a.isSameAddress, setIsSameAddress = _a.setIsSameAddress;
    var deliveryAddresses = useOrderContext().deliveryAddresses;
    var fulfillmentGroup = useFulfillmentGroupContext().fulfillmentGroup;
    var errorRef = useRef(null);
    var rawCurrentAddress = extractAddressFromFulfillmentGroup(fulfillmentGroup.shippingAndHandling ||
        fulfillmentGroup.storePickup ||
        fulfillmentGroup.bodfsDeliveryAndHandling, fulfillmentGroup.fulfillmentType);
    var currentAddress = useMemo(function () {
        if (isAddressEmpty(rawCurrentAddress)) {
            return null;
        }
        return __assign(__assign({ id: 'current' }, rawCurrentAddress), { isDefault: false, isCurrent: true });
    }, []);
    var addressesToShow = useMemo(function () {
        if (deliveryAddresses.length > 0) {
            return getAddressesToShow(deliveryAddresses, currentAddress);
        }
        return [];
    }, [deliveryAddresses]);
    useEffect(function () {
        if (addressesToShow.length > 0) {
            setSelectedAddressId(addressesToShow[0].id);
            setFirstName(addressesToShow[0].firstName || '');
            setLastName(addressesToShow[0].lastName || '');
            setStreetAddress(addressesToShow[0].addressLine1 || '');
            setAddressLineTwo(addressesToShow[0].addressLine2 || null);
            setZipCode(addressesToShow[0].zipCode || '');
            setCityState("".concat(addressesToShow[0].city || '', ", ").concat(addressesToShow[0].state || ''));
            setCountry(addressesToShow[0].country || '');
            setPhoneNumber(rawCurrentAddress.phoneNumber);
            setIsDefault(addressesToShow[0].isDefault || false);
        }
    }, [addressesToShow]);
    useEffect(function () {
        var _a, _b;
        if ((isExcludedAddress || isSameAddress) && errorRef.current) {
            errorRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            (_b = (_a = errorRef.current).focus) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
    }, [isExcludedAddress, isSameAddress]);
    var handleAddNewAddress = function () {
        handleClearAll();
        setIsAddressList(false);
        setPhoneNumber(rawCurrentAddress.phoneNumber);
    };
    var handleAddressChange = function (_event, id) {
        setSelectedAddressId(id);
        var selectedAddr = addressesToShow.find(function (addr) { return addr.id === id; });
        if (selectedAddr) {
            setIsSameAddress(false);
            setFirstName(selectedAddr.firstName || '');
            setLastName(selectedAddr.lastName || '');
            setStreetAddress(selectedAddr.addressLine1 || '');
            setAddressLineTwo(selectedAddr.addressLine2 || null);
            setZipCode(selectedAddr.zipCode || '');
            setCityState("".concat(selectedAddr.city || '', ", ").concat(selectedAddr.state || ''));
            setCountry(selectedAddr.country || '');
            setPhoneNumber(rawCurrentAddress.phoneNumber);
            setIsDefault(selectedAddr.isDefault || false);
        }
    };
    var renderBadges = function (item) {
        var badgeLabel = null;
        if (item.isCurrent) {
            badgeLabel = 'Current';
        }
        else if (item.isDefault) {
            badgeLabel = 'Default';
        }
        return badgeLabel ? (React.createElement("div", { className: "sui-flex sui-flex-col sui-items-start sui-flex-shrink-0" },
            React.createElement(Badge, { variant: "regular", size: "small", color: "medium" }, badgeLabel))) : null;
    };
    return (React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-4" },
        isExcludedAddress && (React.createElement("div", { ref: errorRef, tabIndex: -1 },
            React.createElement(AlertBanner, { status: "error" }, EditDeliveryAddressErrorMessages.EXCLUDED_ADDRESS_SAVED_ADDRESSES))),
        isSameAddress && (React.createElement("div", { ref: errorRef, tabIndex: -1 },
            React.createElement(AlertBanner, { status: "error" },
                React.createElement(Typography, null, EditDeliveryAddressErrorMessages.SAME_ADDRESS_SAVED_ADDRESSES)))),
        React.createElement(Typography, null,
            "You\u2019ll continue to receive order updates at your phone number:",
            ' ',
            React.createElement("span", { className: "sui-font-bold", "data-testid": "delivery-date" }, rawCurrentAddress.phoneNumber)),
        React.createElement(TileGroup, { value: selectedAddressId, onChange: handleAddressChange, "aria-label": "rate", className: "sui-flex sui-flex-col sui-gap-5" }, addressesToShow.length > 0 &&
            addressesToShow.map(function (item) { return (React.createElement(Tile, { key: item.id, value: item.id, "aria-label": "grid view", grow: true },
                React.createElement("div", { className: "sui-flex sui-flex-row sui-justify-between sui-gap-4 sui-w-full sui-p-3" },
                    React.createElement("div", { className: "sui-min-w-0 sui-max-w-full sui-break-words" },
                        React.createElement("div", { className: "sui-mb-3" },
                            React.createElement(Typography, { align: "left", weight: "bold", variant: "h4", height: "tight" },
                                item.firstName,
                                " ",
                                item.lastName)),
                        React.createElement(Typography, { align: "left" },
                            item.addressLine1,
                            React.createElement("br", null),
                            item.city,
                            ", ",
                            item.state,
                            " ",
                            item.zipCode,
                            React.createElement("br", null))),
                    renderBadges(item)))); })),
        React.createElement(Button, { variant: "tertiary", fullWidth: true, onClick: handleAddNewAddress }, "Add New Address")));
};
